import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Phone, MessageCircle, Clock, MapPin, Plus, Minus, Stethoscope, FileText, IndianRupee, Shield } from "lucide-react";
import SEO from "@/components/SEO";
import {
  PHONE_E164 as PHONE_NUMBER,
  WHATSAPP_URL as WHATSAPP_LINK,
  PHONE_DISPLAY,
  GOOGLE_MAPS_URL,
  CLINIC_MAP_EMBED_QUERY,
  CLINIC_ADDRESS_DISPLAY,
  CLINIC_ADDRESS_STREET,
} from "@/constants/contact";

// Hero Section
const HeroSection = () => (
  <section className="bg-warm-white pt-24 pb-12" data-testid="contact-hero">
    <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
      <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl text-premium-purple mb-3 sm:mb-4 leading-tight px-1">Book a Consultation with Dr. Vicky Jain</h1>
      <p className="text-base sm:text-lg text-deep-navy/70 leading-reading max-w-2xl mx-auto px-1">Every treatment begins with a conversation. You leave with clarity — on what is possible, what is appropriate, and what the realistic outcome looks like.</p>
    </div>
  </section>
);

// Contact Form Section
const PROCEDURE_CATEGORIES = {
  "Cosmetic Surgery": [
    "Rhinoplasty",
    "Breast Augmentation",
    "Breast Implant Removal",
    "Breast Lift",
    "Breast Reduction",
    "Breast Fat Transfer",
    "Male Nipple Reduction",
    "Inverted Nipple Correction",
    "Liposuction",
    "Tummy Tuck",
    "Facelift",
    "Neck Lift",
    "Double Chin Removal",
    "Otoplasty",
    "Earlobe Repair",
    "Blepharoplasty",
    "Dimple Creation",
    "Buccal Fat Removal",
    "Gynecomastia",
    "Mommy Makeover",
  ],
  "Reconstructive Surgery": [
    "Fibroadenoma",
    "Axillary Breast Removal",
    "Scar Management",
    "Burns & Burn Scar Treatment",
    "Skin Grafting Surgery",
    "Post-Burn Contracture",
    "Cleft Lip & Palate",
    "Craniosynostosis",
    "Ear Deformities",
    "Hypertrophic Scars",
    "Limb Salvage",
    "Replantation Surgery",
    "Diabetic Foot",
    "Migraine Surgery",
  ],
  "Non-Surgical Treatment": [
    "Thread Lift",
    "Dermal Fillers",
    "Liquid Rhinoplasty",
    "Botox Treatment",
    "Chemical Peels",
    "PRP Therapy",
    "Spider Vein Treatment",
    "Hair Transplant",
    "Scalp Reduction",
  ],
};

const WEB3FORMS_ACCESS_KEY = process.env.REACT_APP_WEB3FORMS_KEY || "fab93ce6-d068-4140-929c-82d11de1f514";

const ContactFormSection = () => {
  const [formData, setFormData] = useState({
    fullName: '', phone: '', email: '', category: '', procedure: '', preferredDate: '', message: '', botcheck: ''
  });
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'category') {
      setFormData({ ...formData, category: value, procedure: '' });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const openWhatsAppFallback = () => {
    const message = encodeURIComponent(
      `New Consultation Request:\n\nName: ${formData.fullName}\nPhone: ${formData.phone}\nEmail: ${formData.email}\nCategory: ${formData.category}\nProcedure: ${formData.procedure}\nPreferred Date: ${formData.preferredDate}\nMessage: ${formData.message}`
    );
    window.open(`${WHATSAPP_LINK}?text=${message}`, '_blank');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (status === 'submitting') return;
    setStatus('submitting');
    setErrorMessage('');

    const payload = {
      access_key: WEB3FORMS_ACCESS_KEY,
      subject: `New Consultation Request — ${formData.fullName}`,
      from_name: "Dr. Vicky Jain Website",
      replyto: formData.email || undefined,
      autoresponse: `Dear ${formData.fullName},\n\nThank you for reaching out to Dr. Vicky Jain's clinic. We have received your consultation request and will get back to you within 24 hours.\n\nIf you need immediate assistance, please call us at +91 96194 89446 or WhatsApp us.\n\nWarm regards,\nDr. Vicky Jain's Team`,
      botcheck: formData.botcheck,
      "Full Name": formData.fullName,
      "Phone Number": formData.phone,
      "Email": formData.email,
      "Category": formData.category,
      "Procedure of Interest": formData.procedure,
      "Preferred Date": formData.preferredDate,
      "Message": formData.message,
    };

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (data.success) {
        setStatus('success');
        setFormData({
          fullName: '', phone: '', email: '', category: '', procedure: '', preferredDate: '', message: '', botcheck: ''
        });
      } else {
        throw new Error(data.message || "Submission failed");
      }
    } catch (err) {
      setStatus('error');
      setErrorMessage(err.message || "Something went wrong. Please try again or contact us on WhatsApp.");
    }
  };

  const proceduresForCategory = formData.category ? PROCEDURE_CATEGORIES[formData.category] : [];

  return (
    <section className="bg-warm-white py-12 md:py-16" data-testid="contact-form-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left Column - Form */}
          <div className="bg-white p-8 border border-[#E8E2D9]">
            <h2 className="font-serif text-2xl text-premium-purple mb-5 md:mb-6 font-semibold tracking-tight">Request a Consultation</h2>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="contact-fullname" className="block text-sm font-semibold text-deep-navy mb-1.5 leading-snug">Full Name *</label>
                <input
                  id="contact-fullname"
                  type="text" name="fullName" required aria-required="true"
                  value={formData.fullName} onChange={handleChange}
                  autoComplete="name"
                  className="w-full px-4 py-3 border border-[#E8E2D9] bg-warm-white focus:outline-none focus:border-gold focus:ring-2 focus:ring-gold/30 transition-colors"
                  placeholder="Your full name"
                  data-testid="input-fullname"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="contact-phone" className="block text-sm font-semibold text-deep-navy mb-1.5 leading-snug">Phone Number *</label>
                  <input
                    id="contact-phone"
                    type="tel" name="phone" required aria-required="true"
                    value={formData.phone} onChange={handleChange}
                    autoComplete="tel"
                    className="w-full px-4 py-3 border border-[#E8E2D9] bg-warm-white focus:outline-none focus:border-gold focus:ring-2 focus:ring-gold/30 transition-colors"
                    placeholder="+91 XXXXX XXXXX"
                    data-testid="input-phone"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-semibold text-deep-navy mb-1.5 leading-snug">Email</label>
                  <input
                    id="contact-email"
                    type="email" name="email" value={formData.email} onChange={handleChange}
                    autoComplete="email"
                    className="w-full px-4 py-3 border border-[#E8E2D9] bg-warm-white focus:outline-none focus:border-gold focus:ring-2 focus:ring-gold/30 transition-colors"
                    placeholder="your@email.com"
                    data-testid="input-email"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="contact-category" className="block text-sm font-semibold text-deep-navy mb-1.5 leading-snug">Category</label>
                <select
                  id="contact-category"
                  name="category" value={formData.category} onChange={handleChange}
                  className="w-full px-4 py-3 border border-[#E8E2D9] bg-warm-white focus:outline-none focus:border-gold focus:ring-2 focus:ring-gold/30 transition-colors cursor-pointer"
                  data-testid="select-category"
                >
                  <option value="">Select a category</option>
                  {Object.keys(PROCEDURE_CATEGORIES).map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="contact-procedure" className="block text-sm font-semibold text-deep-navy mb-1.5 leading-snug">Procedure of Interest</label>
                  <select
                    id="contact-procedure"
                    name="procedure" value={formData.procedure} onChange={handleChange}
                    disabled={!formData.category}
                    aria-disabled={!formData.category}
                    className="w-full px-4 py-3 border border-[#E8E2D9] bg-warm-white focus:outline-none focus:border-gold focus:ring-2 focus:ring-gold/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                    data-testid="select-procedure"
                  >
                    <option value="">{formData.category ? "Select a procedure" : "Select category first"}</option>
                    {proceduresForCategory.map((proc) => (
                      <option key={proc} value={proc}>{proc}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="contact-date" className="block text-sm font-semibold text-deep-navy mb-1.5 leading-snug">Preferred Date</label>
                  <input
                    id="contact-date"
                    type="date" name="preferredDate" value={formData.preferredDate} onChange={handleChange}
                    className="w-full px-4 py-3 border border-[#E8E2D9] bg-warm-white focus:outline-none focus:border-gold focus:ring-2 focus:ring-gold/30 transition-colors"
                    data-testid="input-date"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="contact-message" className="block text-sm font-semibold text-deep-navy mb-1.5 leading-snug">Message</label>
                <textarea
                  id="contact-message"
                  name="message" rows="4" value={formData.message} onChange={handleChange}
                  className="w-full px-4 py-3 border border-[#E8E2D9] bg-warm-white focus:outline-none focus:border-gold focus:ring-2 focus:ring-gold/30 transition-colors resize-none"
                  placeholder="Tell us about your goals or any questions you have..."
                  data-testid="textarea-message"
                />
              </div>

              {/* Honeypot — keep empty; hidden from real users */}
              <input
                type="text"
                name="botcheck"
                value={formData.botcheck}
                onChange={handleChange}
                tabIndex="-1"
                autoComplete="off"
                aria-hidden="true"
                style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
              />

              {status === 'success' && (
                <div
                  role="status"
                  className="border border-green-600/30 bg-green-50 text-green-800 px-4 py-3 text-sm"
                  data-testid="form-success"
                >
                  Thank you — your request has been sent. Our team will reach out within 1 business day. For urgent queries, call {PHONE_DISPLAY} or WhatsApp us.
                </div>
              )}

              {status === 'error' && (
                <div
                  role="alert"
                  className="border border-red-600/30 bg-red-50 text-red-800 px-4 py-3 text-sm"
                  data-testid="form-error"
                >
                  {errorMessage || "Something went wrong."}{' '}
                  <button
                    type="button"
                    onClick={openWhatsAppFallback}
                    className="underline font-semibold hover:text-red-900"
                  >
                    Send on WhatsApp instead
                  </button>
                </div>
              )}

              <Button
                type="submit"
                disabled={status === 'submitting'}
                className="w-full bg-gold text-white hover:bg-gold-hover border-none rounded-none px-8 py-6 text-base disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="submit-btn"
              >
                {status === 'submitting' ? 'Sending…' : 'Book Consultation'}
              </Button>
            </form>
          </div>

          {/* Right Column - Clinic Info */}
          <div className="space-y-8">
            <div>
              <h2 className="font-serif text-2xl text-premium-purple mb-6">Contact Information</h2>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-none bg-gold/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-gold" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-deep-navy mb-1 tracking-wide uppercase">Phone</p>
                    <a href={`tel:${PHONE_NUMBER}`} className="text-deep-navy/70 hover:text-gold transition-colors">{PHONE_DISPLAY}</a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-none bg-gold/10 flex items-center justify-center flex-shrink-0">
                    <MessageCircle className="w-6 h-6 text-gold" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-deep-navy mb-1 tracking-wide uppercase">WhatsApp</p>
                    <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="text-deep-navy/70 hover:text-gold transition-colors">Chat with us on WhatsApp</a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-none bg-gold/10 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-gold" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-deep-navy mb-1 tracking-wide uppercase">Clinic Hours</p>
                    <p className="text-deep-navy/70">Monday – Friday: 8:00 AM – 8:00 PM</p>
                    <p className="text-deep-navy/70">Saturday: 9:00 AM – 7:00 PM</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-none bg-gold/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-gold" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-deep-navy mb-1 tracking-wide uppercase">Location</p>
                    <a
                      href={GOOGLE_MAPS_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-deep-navy/70 hover:text-gold transition-colors"
                    >
                      {CLINIC_ADDRESS_DISPLAY}
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-3" data-testid="contact-map">
              <div className="relative w-full overflow-hidden border border-[#E8E2D9] aspect-[4/3] min-h-[240px] bg-[#E8E2D9]">
                <iframe
                  title="Clinic location on Google Maps"
                  className="absolute inset-0 h-full w-full border-0"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                  src={`https://www.google.com/maps?q=${encodeURIComponent(CLINIC_MAP_EMBED_QUERY)}&output=embed`}
                />
              </div>
              <a
                href={GOOGLE_MAPS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm font-medium text-deep-navy hover:text-gold transition-colors"
              >
                <MapPin className="w-4 h-4 text-gold shrink-0" aria-hidden />
                Open in Google Maps
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// What to Expect Section
const WhatToExpectSection = () => {
  const expectations = [
    { title: "Clinical Discussion", description: "This is a clinical discussion — not a sales appointment.", icon: Stethoscope },
    { title: "Honest Assessment", description: "You receive an honest, realistic picture of what is possible.", icon: FileText },
    { title: "Transparent Pricing", description: "You get a fully itemised estimate with no hidden fees.", icon: IndianRupee }
  ];
  return (
    <section className="bg-warm-white py-10 md:py-14" data-testid="what-to-expect-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">What to Expect at Your Consultation</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {expectations.map((item, index) => (
            <div key={index} className="bg-white p-8 border border-[#E8E2D9] text-center" data-testid={`expect-card-${index}`}>
              <div className="w-14 h-14 rounded-none bg-gold/10 flex items-center justify-center mx-auto mb-6">
                <item.icon className="w-7 h-7 text-gold" />
              </div>
              <h3 className="font-serif text-xl text-premium-purple mb-3 font-semibold tracking-tight">{item.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{item.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// FAQ Strip Section
const FAQStripSection = () => {
  const faqs = [
    { question: "How do I book a consultation?", answer: "You can book a consultation by filling out the form on this page, calling us at +91-9619489446, or sending a WhatsApp message. Our team will respond within 24 hours to confirm your appointment." },
    { question: "Can I get a cost estimate before meeting Dr. Jain?", answer: "We can provide general price ranges over the phone, but accurate quotes require an in-person consultation where Dr. Jain can assess your specific case, discuss options, and create a tailored treatment plan." },
    { question: "What should I bring to my consultation?", answer: "Bring a list of your current medications, any relevant medical records, photos of desired results if you have them, and a list of questions. Most importantly, bring an open mind ready for honest discussion." },
    { question: "Is my consultation confidential?", answer: "Absolutely. All consultations are completely confidential. Dr. Jain's practice adheres to strict medical privacy standards. Your personal information and medical details are never shared without your explicit consent." }
  ];
  return (
    <section className="bg-[#FAFAF8] py-10 md:py-14" data-testid="faq-strip-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Quick Questions</h2>
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="border-b border-deep-navy/10 bg-white mb-2">
                <AccordionTrigger className="text-left font-serif text-base font-semibold text-premium-purple hover:no-underline hover:text-gold py-5 px-6 [&[data-state=open]>div>svg:first-child]:hidden [&[data-state=open]>div>svg:last-child]:block [&>svg]:hidden">
                  <span className="pr-4">{faq.question}</span>
                  <div className="flex-shrink-0"><Plus className="w-5 h-5 text-gold" /><Minus className="w-5 h-5 text-gold hidden" /></div>
                </AccordionTrigger>
                <AccordionContent className="text-deep-navy/70 leading-relaxed pb-5 px-6">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
};

const contactStructuredData = {
  "@context": "https://schema.org",
  "@type": "MedicalClinic",
  name: "Dr. Vicky Jain — Plastic & Reconstructive Surgery",
  url: "https://drvickyjain.com/contact",
  telephone: "+91-9619489446",
  image: "https://drvickyjain.com/dr-jain.avif",
  hasMap: GOOGLE_MAPS_URL,
  address: {
    "@type": "PostalAddress",
    streetAddress: CLINIC_ADDRESS_STREET,
    addressLocality: "Mumbai",
    addressRegion: "Maharashtra",
    postalCode: "400004",
    addressCountry: "IN",
  },
  openingHours: "Mo-Sa 10:00-19:00",
  priceRange: "₹₹₹",
  medicalSpecialty: "PlasticSurgery",
};

const ContactPage = () => (
  <>
    <SEO title="Book a Consultation — Dr. Vicky Jain Plastic Surgery, Mumbai" description="Book a consultation with Dr. Vicky Jain, plastic surgeon. Fill out our form, call, or WhatsApp us to schedule your appointment." canonical="/contact" />
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(contactStructuredData) }} />
    <HeroSection />
    <ContactFormSection />
    <WhatToExpectSection />
    <FAQStripSection />
  </>
);

export default ContactPage;
